import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import journalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import VemsDeclaredJournalEvent from 'parog-web/models/vems-declared-journal-event';
import type SemeiaSessionService from 'parog-web/services/session';

export default class VemsEditionFormComponent extends CrudJournalEventBaseComponent<VemsDeclaredJournalEvent> {
  static override forcDisplayName = 'Exploration fonctionnelle respiratoire';
  static override fullComponentId = 'vems/edition-form';
  static override journalEventClass = VemsDeclaredJournalEvent;

  @service declare flashMessages: FlashMessagesService;
  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;

  override retrieveJournalEvent =
    journalEventRetrievalStrategies.sourceJournalEvent;

  get isFromSpirometer() {
    return this.changeset.get('source') != 'wise';
  }

  override get model(): typeof this.journalEventClass {
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    const model = super.model;

    if (this.isNewMode) {
      model.occurredAt = new Date();
      model.source = 'wise';
    }

    return model;
  }

  override get toastMessage() {
    return this.session.patient
      ? {
          message: 'Votre mesure a bien été prise en compte.',
          title: 'Spirométrie'
        }
      : super.toastMessage;
  }

  override get validations() {
    return this.journalEventClass.validations(this.intl);
  }

  override save = async () => {
    await super.save();
    this.analytics.trackPatientMeasurement('vems-demm');
  };

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        {{#if this.isFromSpirometer}}
          <p>Ces mesures proviennent du spiromètre connecté du patient, elles ne
            peuvent pas être modifiées.</p>
          <p>Vous pouvez ajouter un commentaire.</p>
        {{/if}}
        <div class='field'>
          <Form.Date
            @labelName='Date de la mesure'
            @propertyName='occurredAt'
            @isReadOnly={{this.isFromSpirometer}}
          />
        </div>
        <div class='field'>
          <Form.NumberInput
            @min='0'
            @max='5'
            @labelName='VEMS (FEV1) (L): Mesure 1'
            @placeholder='1-5L'
            @propertyName='value1'
            @isReadOnly={{this.isFromSpirometer}}
          />
        </div>
        <div class='field'>
          <Form.NumberInput
            @min='0'
            @max='5'
            @labelName='VEMS (FEV1) (L): Mesure 2'
            @placeholder='1-5L'
            @propertyName='value2'
            @isReadOnly={{this.isFromSpirometer}}
          />
        </div>
        <div class='field'>
          <Form.NumberInput
            @min='0'
            @max='5'
            @labelName='VEMS (FEV1) (L): Mesure 3'
            @placeholder='1-5L'
            @propertyName='value3'
            @isReadOnly={{this.isFromSpirometer}}
          />
        </div>
        <div class='field'>
          <Form.TextArea @labelName='Commentaires' @propertyName='comments' />
        </div>
        {{#if this.session.patient}}
          <div class='mb-8'>
            Vous avez des questions sur comment mesurer votre VEMS ? Contacter
            votre équipe médicale avec la
            <LinkTo @route='patient-web.protected.chat'>messagerie de
              l'application</LinkTo>.
          </div>
        {{/if}}
      </:content>
    </Provider>
  </template>
}
